import type {
  FormEvent,
  KeyboardEventHandler,
  RefObject
} from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  AutopilotMode,
  AppGitlabInfo,
  Conversation,
  ConversationAttachment,
  ConversationGitlabInfo,
  ModelConfig
} from '../types';

export function canRenderConversationGitlab(
  canReadCode: boolean,
  gitlabInfo: ConversationGitlabInfo | null
): gitlabInfo is ConversationGitlabInfo {
  return canReadCode && gitlabInfo !== null;
}
import type { ConversationInputDraftVersion } from '../utils/drafts';

type ConversationHeaderProps = {
  appId: string;
  conversation: Conversation | null;
  editingTitle: boolean;
  editingTitleValue: string;
  editingTitleBusy: boolean;
  effectiveConversationStatus: string;
  isArchived: boolean;
  gitlabInfo: ConversationGitlabInfo | null;
  multiGitlabApps: AppGitlabInfo[];
  gitlabBusy: boolean;
  gitlabError: string | null;
  squashOnMerge: boolean;
  runnerStateMessage: string;
  runnerProgressLabel: number | null;
  interactionModeBusy: boolean;
  canManageConversation: boolean;
  canManageSecrets: boolean;
  canManageTools: boolean;
  canReadCode: boolean;
  canAttachTerminal: boolean;
  canProposeCodeChanges: boolean;
  canMergeCodeChanges: boolean;
  onTitleChange: (value: string) => void;
  onTitleKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onSaveTitle: () => void;
  onStartEditingTitle: () => void;
  onCancelEditingTitle: () => void;
  onShowSecrets: () => void;
  onShowToolAccess: () => void;
  onShowTerminal: () => void;
  onToggleArchive: () => void;
  onCreateMergeRequest: () => void;
  onMergeMergeRequest: () => void;
  onSetSquashOnMerge: (checked: boolean) => void;
  onShowRunnerDetails: () => void;
  onSetInteractionMode: (mode: 'chat' | 'interactive_agent') => void;
  pipelineStatusClass: (status?: string | null) => string;
  mrStatusClass: (status?: string | null) => string;
};

export function ConversationHeader({
  appId,
  conversation,
  editingTitle,
  editingTitleValue,
  editingTitleBusy,
  effectiveConversationStatus,
  isArchived,
  gitlabInfo,
  multiGitlabApps,
  gitlabBusy,
  gitlabError,
  squashOnMerge,
  runnerStateMessage,
  runnerProgressLabel,
  interactionModeBusy,
  canManageConversation,
  canManageSecrets,
  canManageTools,
  canReadCode,
  canAttachTerminal,
  canProposeCodeChanges,
  canMergeCodeChanges,
  onTitleChange,
  onTitleKeyDown,
  onSaveTitle,
  onStartEditingTitle,
  onCancelEditingTitle,
  onShowSecrets,
  onShowToolAccess,
  onShowTerminal,
  onToggleArchive,
  onCreateMergeRequest,
  onMergeMergeRequest,
  onSetSquashOnMerge,
  onShowRunnerDetails,
  onSetInteractionMode,
  pipelineStatusClass,
  mrStatusClass
}: ConversationHeaderProps) {
  const secondaryApps = multiGitlabApps.length > 1 ? multiGitlabApps.slice(1) : [];
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);
  const previousMrs = gitlabInfo?.previous_merge_requests ?? [];
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const runMobileAction = (action: () => void) => {
    setMobileActionsOpen(false);
    action();
  };

  return (
    <>
      <div className="card-header conversation-header">
        <div className="conversation-header__title">
          {editingTitle && canManageConversation ? (
            <div className="row">
              <input
                type="text"
                value={editingTitleValue}
                onChange={(event) => onTitleChange(event.target.value)}
                onKeyDown={onTitleKeyDown}
                disabled={editingTitleBusy}
                autoFocus
                className="conversation-title-edit"
              />
              <button type="button" onClick={onSaveTitle} disabled={editingTitleBusy}>
                Save
              </button>
              <button
                type="button"
                className="ghost"
                onClick={onCancelEditingTitle}
                disabled={editingTitleBusy}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="conversation-title-row">
              <h2>{conversation?.title ?? 'Conversation'}</h2>
              {canManageConversation && <button
                type="button"
                onClick={onStartEditingTitle}
                title="Edit title"
                aria-label="Edit conversation title"
                className="ghost icon-button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
              </button>}
              {effectiveConversationStatus && (
                <span className="badge">{effectiveConversationStatus}</span>
              )}
            </div>
          )}
        </div>
        <div className="row conversation-header__actions">
          <select
            aria-label="Conversation experience"
            value={conversation?.interaction_mode ?? 'chat'}
            disabled={!conversation || interactionModeBusy || !canManageConversation}
            onChange={(event) =>
              onSetInteractionMode(event.target.value as 'chat' | 'interactive_agent')
            }
          >
            <option value="chat">Chat</option>
            <option value="interactive_agent">Interactive agent</option>
          </select>
          <Link to="/conversations" className="link-button secondary">
            Back to conversations
          </Link>
          {(conversation?.app_id || appId) && (
            <Link to={`/apps/${conversation?.app_id ?? appId}`} className="link-button secondary">
              View app
            </Link>
          )}
          {(conversation?.app_id || appId) && (
            <span className="text-muted conversation-agent-hint">
              Add <code className="code-inline">deploy/agent.yaml</code> to customize the
              agent
            </span>
          )}
          {canManageSecrets && <button type="button" className="ghost" onClick={onShowSecrets}>
            Secrets
          </button>}
          {canManageTools && <button type="button" className="ghost" onClick={onShowToolAccess}>
            Tools (MCP)
          </button>}
          {canManageConversation && <button
            type="button"
            className="ghost"
            onClick={onToggleArchive}
            title={
              isArchived
                ? 'Unarchive conversation'
                : 'Archive conversation (closes associated MR)'
            }
          >
            {isArchived ? 'Unarchive' : 'Archive'}
          </button>}
        </div>
        <div className="conversation-header__mobile-actions">
          <button
            type="button"
            className="secondary conversation-header__mobile-actions-trigger"
            aria-expanded={mobileActionsOpen}
            aria-controls="conversation-mobile-actions"
            onClick={() => setMobileActionsOpen((open) => !open)}
          >
            Actions
            <span aria-hidden="true">{mobileActionsOpen ? '\u25b2' : '\u25bc'}</span>
          </button>
          {mobileActionsOpen && (
            <div id="conversation-mobile-actions" className="conversation-header__mobile-actions-menu">
              <Link to="/conversations" className="link-button secondary">
                Conversations
              </Link>
              {(conversation?.app_id || appId) && (
                <Link
                  to={`/apps/${conversation?.app_id ?? appId}`}
                  className="link-button secondary"
                >
                  View app
                </Link>
              )}
              {canManageSecrets && <button
                type="button"
                className="ghost"
                onClick={() => runMobileAction(onShowSecrets)}
              >
                Secrets
              </button>}
              {canManageTools && <button
                type="button"
                className="ghost"
                onClick={() => runMobileAction(onShowToolAccess)}
              >
                Tools (MCP)
              </button>}
              {canAttachTerminal && <button
                type="button"
                className="ghost"
                onClick={() => runMobileAction(onShowTerminal)}
              >
                Terminal
              </button>}
              {canManageConversation && <button
                type="button"
                className="ghost"
                onClick={() => runMobileAction(onToggleArchive)}
                title={
                  isArchived
                    ? 'Unarchive conversation'
                    : 'Archive conversation (closes associated MR)'
                }
              >
                {isArchived ? 'Unarchive' : 'Archive'}
              </button>}
            </div>
          )}
        </div>
      </div>

      {canRenderConversationGitlab(canReadCode, gitlabInfo) && (
        <div className="conversation-gitlab">
          <div className="conversation-gitlab__row">
            <div className="conversation-gitlab__block">
              <span className="conversation-gitlab__label">Merge Request</span>
              {gitlabInfo.merge_request ? (
                <div className="conversation-gitlab__value-row">
                  <a
                    className="conversation-gitlab__mr-link"
                    href={gitlabInfo.merge_request.web_url}
                    target="_blank"
                    rel="noreferrer"
                    title={`!${gitlabInfo.merge_request.iid} ${gitlabInfo.merge_request.title}`}
                  >
                    <span className="conversation-gitlab__mr-id">
                      !{gitlabInfo.merge_request.iid}
                    </span>
                    <span className="conversation-gitlab__mr-title">
                      {gitlabInfo.merge_request.title}
                    </span>
                  </a>
                  <span className={mrStatusClass(gitlabInfo.merge_request.state)}>
                    {gitlabInfo.merge_request.state ?? 'unknown'}
                  </span>
                </div>
              ) : (
                <span className="text-muted">
                  {gitlabInfo.branch_exists ? 'No MR yet' : 'Branch not pushed'}
                </span>
              )}
            </div>
            <div className="conversation-gitlab__block">
              <span className="conversation-gitlab__label">Latest build</span>
              {gitlabInfo.latest_pipeline ? (
                <a
                  href={gitlabInfo.latest_pipeline.web_url}
                  target="_blank"
                  rel="noreferrer"
                  className={`${pipelineStatusClass(gitlabInfo.latest_pipeline.status)} conversation-gitlab__pipeline-link`}
                >
                  {gitlabInfo.latest_pipeline.status}
                </a>
              ) : (
                <span className="text-muted">No pipeline</span>
              )}
            </div>
            <div className="conversation-gitlab__actions">
              {canProposeCodeChanges && gitlabInfo.can_create_mr && (
                <button
                  type="button"
                  className="ghost"
                  onClick={onCreateMergeRequest}
                  disabled={gitlabBusy}
                >
                  {gitlabBusy ? 'Creating...' : 'Create MR'}
                </button>
              )}
              {canMergeCodeChanges && gitlabInfo.can_merge && (
                <div className="merge-actions">
                  <label className="squash-checkbox">
                    <input
                      type="checkbox"
                      checked={squashOnMerge}
                      onChange={(event) => onSetSquashOnMerge(event.target.checked)}
                      disabled={gitlabBusy}
                    />
                    Squash commits
                  </label>
                  <button type="button" onClick={onMergeMergeRequest} disabled={gitlabBusy}>
                    {gitlabBusy ? 'Merging...' : 'Merge MR'}
                  </button>
                </div>
              )}
            </div>
          </div>
          {gitlabError && <div className="text-muted">GitLab: {gitlabError}</div>}
          {secondaryApps.length === 1 && (() => {
            const appEntry = secondaryApps[0];
            return (
              <div className="conversation-gitlab__secondary conversation-gitlab__secondary--inline">
                <div className="conversation-gitlab__secondary-row">
                  <span className="conversation-gitlab__secondary-app-name">{appEntry.app_name}</span>
                  {appEntry.gitlab?.merge_request ? (
                    <>
                      <a
                        className="conversation-gitlab__mr-link"
                        href={appEntry.gitlab.merge_request.web_url}
                        target="_blank"
                        rel="noreferrer"
                        title={`!${appEntry.gitlab.merge_request.iid} ${appEntry.gitlab.merge_request.title}`}
                      >
                        <span className="conversation-gitlab__mr-id">
                          !{appEntry.gitlab.merge_request.iid}
                        </span>
                      </a>
                      <span className={mrStatusClass(appEntry.gitlab.merge_request.state)}>
                        {appEntry.gitlab.merge_request.state ?? 'unknown'}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted">
                      {appEntry.gitlab?.branch_exists ? 'No MR' : 'No branch'}
                    </span>
                  )}
                  {appEntry.gitlab?.latest_pipeline ? (
                    <a
                      href={appEntry.gitlab.latest_pipeline.web_url}
                      target="_blank"
                      rel="noreferrer"
                      className={pipelineStatusClass(appEntry.gitlab.latest_pipeline.status)}
                    >
                      {appEntry.gitlab.latest_pipeline.status}
                    </a>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              </div>
            );
          })()}
          {secondaryApps.length > 1 && (
            <div className="conversation-gitlab__secondary">
              <button
                type="button"
                className="ghost conversation-gitlab__toggle"
                onClick={() => setSecondaryExpanded((prev) => !prev)}
              >
                <span>{secondaryExpanded ? 'Hide' : `+${secondaryApps.length} apps`}</span>
                <span className={`chevron ${secondaryExpanded ? 'chevron--up' : 'chevron--down'}`} />
              </button>
              {secondaryExpanded && (
                <div className="conversation-gitlab__secondary-list">
                  {secondaryApps.map((appEntry) => (
                    <div key={appEntry.app_id} className="conversation-gitlab__secondary-row">
                      <span className="conversation-gitlab__secondary-app-name">{appEntry.app_name}</span>
                      {appEntry.gitlab?.merge_request ? (
                        <>
                          <a
                            className="conversation-gitlab__mr-link"
                            href={appEntry.gitlab.merge_request.web_url}
                            target="_blank"
                            rel="noreferrer"
                            title={`!${appEntry.gitlab.merge_request.iid} ${appEntry.gitlab.merge_request.title}`}
                          >
                            <span className="conversation-gitlab__mr-id">
                              !{appEntry.gitlab.merge_request.iid}
                            </span>
                          </a>
                          <span className={mrStatusClass(appEntry.gitlab.merge_request.state)}>
                            {appEntry.gitlab.merge_request.state ?? 'unknown'}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted">
                          {appEntry.gitlab?.branch_exists ? 'No MR' : 'No branch'}
                        </span>
                      )}
                      {appEntry.gitlab?.latest_pipeline ? (
                        <a
                          href={appEntry.gitlab.latest_pipeline.web_url}
                          target="_blank"
                          rel="noreferrer"
                          className={pipelineStatusClass(appEntry.gitlab.latest_pipeline.status)}
                        >
                          {appEntry.gitlab.latest_pipeline.status}
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {previousMrs.length > 0 && (
            <div className="conversation-gitlab__secondary">
              <button
                type="button"
                className="ghost conversation-gitlab__toggle"
                onClick={() => setHistoryExpanded((prev) => !prev)}
              >
                <span>{historyExpanded ? 'Hide history' : `+${previousMrs.length} previous MR${previousMrs.length > 1 ? 's' : ''}`}</span>
                <span className={`chevron ${historyExpanded ? 'chevron--up' : 'chevron--down'}`} />
              </button>
              {historyExpanded && (
                <div className="conversation-gitlab__secondary-list">
                  {previousMrs.map((mr) => (
                    <div key={mr.iid} className="conversation-gitlab__secondary-row">
                      <a
                        className="conversation-gitlab__mr-link"
                        href={mr.web_url}
                        target="_blank"
                        rel="noreferrer"
                        title={`!${mr.iid} ${mr.title}`}
                      >
                        <span className="conversation-gitlab__mr-id">
                          !{mr.iid}
                        </span>
                      </a>
                      <span className={mrStatusClass(mr.state)}>
                        {mr.state ?? 'unknown'}
                      </span>
                      <span className="conversation-gitlab__mr-title">
                        {mr.title}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isArchived && (
        <div className="conversation-archived" role="status" aria-live="polite">
          <div className="conversation-archived__title">Archived conversation</div>
          {conversation?.archived_at && (
            <div className="conversation-archived__meta">
              Archived {new Date(conversation.archived_at).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {runnerStateMessage && (
        <button type="button" className="runner-banner" onClick={onShowRunnerDetails}>
          <span className="runner-banner__pulse" />
          <span className="runner-banner__message" title={runnerStateMessage}>
            {runnerStateMessage}
          </span>
          {runnerProgressLabel !== null && (
            <span className="runner-banner__progress-label">{runnerProgressLabel}%</span>
          )}
          <span className="runner-banner__cta">Details</span>
        </button>
      )}
    </>
  );
}

type ConversationComposerProps = {
  inputRef: RefObject<HTMLTextAreaElement | null>;
  modelMenuRef: RefObject<HTMLDivElement | null>;
  autopilotMenuRef: RefObject<HTMLDivElement | null>;
  draftHistoryMenuRef: RefObject<HTMLDivElement | null>;
  sending: boolean;
  holdQueuedMessages: boolean;
  composerWillQueue: boolean;
  isArchived: boolean;
  inputValue: string;
  draftVersions: ConversationInputDraftVersion[];
  activeDraftVersionId: string | null;
  selectedModelId: string | null;
  models: ModelConfig[];
  modelMenuOpen: boolean;
  draftHistoryOpen: boolean;
  autopilotBusy: boolean;
  autopilotMenuOpen: boolean;
  autopilotMode: AutopilotMode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  onInputChange: (value: string) => void;
  onInputKeyDown: KeyboardEventHandler<HTMLTextAreaElement>;
  onToggleDraftHistory: () => void;
  onSelectDraftVersion: (version: ConversationInputDraftVersion) => void;
  onSetDraftHistoryOpen: (open: boolean) => void;
  onHoldToggle: (checked: boolean) => void;
  onToggleModelMenu: () => void;
  onSelectModel: (modelId: string) => void;
  onSetModelMenuOpen: (open: boolean) => void;
  onToggleAutopilotMenu: () => void;
  onUpdateAutopilotMode: (mode: AutopilotMode) => void | Promise<void>;
  pendingAttachments: ConversationAttachment[];
  attachmentBusy: boolean;
  canPublishArtifacts: boolean;
  canRespond: boolean;
  canManageConversation: boolean;
  onSelectFiles: (files: FileList | null) => void;
  onRemoveAttachment: (attachment: ConversationAttachment) => void;
};

const AUTOPILOT_MODE_LABELS: Record<AutopilotMode, string> = {
  off: 'Off',
  semi: 'Semi',
  full: 'Full'
};

function formatDraftVersionTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });
}

export function ConversationComposer({
  inputRef,
  modelMenuRef,
  autopilotMenuRef,
  draftHistoryMenuRef,
  sending,
  holdQueuedMessages,
  composerWillQueue,
  isArchived,
  inputValue,
  draftVersions,
  activeDraftVersionId,
  selectedModelId,
  models,
  modelMenuOpen,
  draftHistoryOpen,
  autopilotBusy,
  autopilotMenuOpen,
  autopilotMode,
  onSubmit,
  onInputChange,
  onInputKeyDown,
  onToggleDraftHistory,
  onSelectDraftVersion,
  onSetDraftHistoryOpen,
  onHoldToggle,
  onToggleModelMenu,
  onSelectModel,
  onSetModelMenuOpen,
  onToggleAutopilotMenu,
  onUpdateAutopilotMode,
  pendingAttachments,
  attachmentBusy,
  canPublishArtifacts,
  canRespond,
  canManageConversation,
  onSelectFiles,
  onRemoveAttachment
}: ConversationComposerProps) {
  if (!canRespond) {
    return (
      <div className="conversation-input conversation-input--read-only" role="status">
        You have read-only access to this conversation.
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="conversation-input">
      <div className="conversation-input__editor">
        <textarea
          ref={inputRef as RefObject<HTMLTextAreaElement>}
          name="content"
          placeholder={
            holdQueuedMessages
              ? 'Queue a message without starting it'
              : composerWillQueue
                ? 'Queue a message'
                : 'Send a message'
          }
          rows={3}
          value={inputValue}
          onChange={(event) => onInputChange(event.target.value)}
          onKeyDown={onInputKeyDown}
        />
        {canRespond && draftVersions.length > 0 && (
          <div
            className="draft-history"
            ref={draftHistoryMenuRef as RefObject<HTMLDivElement>}
          >
            <button
              type="button"
              className="ghost draft-history__trigger"
              title="Draft history"
              aria-label="Draft history"
              aria-haspopup="menu"
              aria-expanded={draftHistoryOpen}
              onClick={onToggleDraftHistory}
            >
              ↺
            </button>
            {draftHistoryOpen && (
              <div className="draft-history__menu" role="menu" aria-label="Draft history">
                {draftVersions.map((version) => (
                  <button
                    key={version.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={activeDraftVersionId === version.id}
                    className={`ghost draft-history__item${
                      activeDraftVersionId === version.id ? ' is-active' : ''
                    }`}
                    title={version.content}
                    onClick={() => {
                      onSelectDraftVersion(version);
                      onSetDraftHistoryOpen(false);
                    }}
                  >
                    <span className="draft-history__preview">{version.content}</span>
                    <span className="draft-history__time">
                      {formatDraftVersionTime(version.updated_at)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {canRespond && pendingAttachments.length > 0 && (
        <div className="composer-attachments" aria-label="Files attached to this message">
          {pendingAttachments.map((attachment) => (
            <div className="attachment-chip" key={attachment.id}>
              <span className="attachment-chip__name">{attachment.filename}</span>
              <span className="attachment-chip__meta">{formatAttachmentSize(attachment.byte_size)}</span>
              <button
                type="button"
                className="attachment-chip__remove"
                onClick={() => onRemoveAttachment(attachment)}
                aria-label={`Remove ${attachment.filename}`}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="row conversation-input__actions">
        <div className="conversation-input__primary-actions">
          {canPublishArtifacts && <label className="file-attach-button" title="Attach files">
            <input
              type="file"
              multiple
              onChange={(event) => {
                onSelectFiles(event.target.files);
                event.currentTarget.value = '';
              }}
            />
            <span>{attachmentBusy ? 'Attaching...' : 'Attach'}</span>
          </label>}
          {canRespond && <button type="submit">
            {sending
              ? composerWillQueue
                ? 'Queue'
                : 'Send'
              : holdQueuedMessages
                ? 'Queue only'
                : composerWillQueue
                  ? 'Queue'
                  : isArchived
                    ? 'Unarchive and Send'
                    : 'Send'}
          </button>}
        </div>
        {canRespond && <div className="conversation-input__secondary-actions">
          <label
            className="queue-mode-toggle"
            title="Keep new messages queued until started manually"
          >
            <input
              type="checkbox"
              checked={holdQueuedMessages}
              onChange={(event) => onHoldToggle(event.target.checked)}
            />
            <span>Hold</span>
          </label>
          {canManageConversation && <div className="autopilot-inline" ref={autopilotMenuRef as RefObject<HTMLDivElement>}>
            <button
              type="button"
              className="ghost autopilot-inline__trigger"
              aria-haspopup="menu"
              aria-expanded={autopilotMenuOpen}
              aria-label="Autopilot mode"
              disabled={autopilotBusy}
              onClick={onToggleAutopilotMenu}
            >
              <span className="autopilot-inline__label">Auto</span>
              <span className="autopilot-inline__value">
                {AUTOPILOT_MODE_LABELS[autopilotMode]}
              </span>
            </button>
            {autopilotMenuOpen && (
              <div className="autopilot-inline__menu" role="menu" aria-label="Autopilot mode menu">
                {(['off', 'semi', 'full'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="menuitemradio"
                    aria-checked={autopilotMode === mode}
                    className={`autopilot-inline__item${autopilotMode === mode ? ' is-active' : ''}`}
                    disabled={autopilotBusy}
                    onClick={() => {
                      void onUpdateAutopilotMode(mode);
                    }}
                  >
                    <span>{AUTOPILOT_MODE_LABELS[mode]}</span>
                    <span className="autopilot-inline__item-state">
                      {autopilotMode === mode ? 'Current' : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>}
          <div className="model-inline" ref={modelMenuRef as RefObject<HTMLDivElement>}>
            <button
              type="button"
              className="ghost model-inline__trigger"
              aria-haspopup="menu"
              aria-expanded={modelMenuOpen}
              aria-label="Select model"
              disabled={models.length === 0}
              onClick={onToggleModelMenu}
            >
              <span className="model-inline__value">
                {selectedModelId
                  ? models.find((model) => model.id === selectedModelId)?.name ?? 'Model'
                  : 'Model'}
              </span>
            </button>
            {modelMenuOpen && (
              <div className="model-inline__menu" role="menu" aria-label="Model selection menu">
                {models.map((model) => (
                  <button
                    key={model.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selectedModelId === model.id}
                    className={`model-inline__item${selectedModelId === model.id ? ' is-active' : ''}`}
                    onClick={() => {
                      onSelectModel(model.id);
                      onSetModelMenuOpen(false);
                    }}
                  >
                    <span>{model.name}</span>
                    {model.is_default && !model.is_global && (
                      <span className="model-inline__item-badge">Default</span>
                    )}
                    <span className="model-inline__item-state">
                      {selectedModelId === model.id ? '✓' : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>}
      </div>
    </form>
  );
}

function formatAttachmentSize(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
